import React, { useContext, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Icon } from '@iconify/react';
import LoginContext from "../../store/loginContext";
import langContextObj from "../../store/langContext";
import { images } from "../../constants";
import Input from "../UI/input/Input";
import Button from "../UI/button/Button";
import classes from "./Login.module.scss";

// Define our API response types for better type safety
interface LoginResponse {
  success: boolean;
  code?: 'USER_NOT_FOUND' | 'INCORRECT_PASSWORD' | 'LOGIN_SUCCESS';
  message?: string;
}

function LoginBox() {
  // Context and refs setup
  const loginCtx = useContext(LoginContext);
  const langCtx = useContext(langContextObj);
  const userNameRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  const errorMessageRef = useRef<HTMLSpanElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const isLoginRoute =
    location.pathname === "/" || location.pathname === "/login";

  // State management
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Same backend API domain as the rest of the app (only two domains in prod).
  const API_BASE =
    process.env.REACT_APP_API_URL || "https://api.trafertirgroup.com";
  const API_URL = `${API_BASE}/login/`;

  // Define error codes for better maintainability
  const ERROR_CODES = {
    USER_NOT_FOUND: 'USER_NOT_FOUND',
    INCORRECT_PASSWORD: 'INCORRECT_PASSWORD',
    LOGIN_SUCCESS: 'LOGIN_SUCCESS'
  } as const;

  // Main login handler function
  async function handleLogin(e: React.FormEvent) {
    e.preventDefault();
    setErrorMessage("");
    
    const username = userNameRef.current?.value;
    const password = passwordRef.current?.value;

    // Basic validation
    if (!username || !password) {
      setErrorMessage(t("allFieldsRequired"));
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(API_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ username, password }),
      });

      const data: LoginResponse = await response.json();

      if (response.ok && data.success) {
        // Commit login state before navigating so AuthLayout does not bounce back to /login
        flushSync(() => {
          loginCtx.login();
        });

        const rememberMe = document.getElementById("rememberMe") as HTMLInputElement;
        if (rememberMe?.checked) {
          localStorage.setItem("isLoggedIn", "true");
          localStorage.setItem("username", username);
        }

        navigate("/dashboard", { replace: true });
      } else {
        // Handle different error cases
        switch (data.code) {
          case ERROR_CODES.USER_NOT_FOUND:
            setErrorMessage(t("userNotFound"));
            userNameRef.current?.focus();
            break;
            
          case ERROR_CODES.INCORRECT_PASSWORD:
            setErrorMessage(t("incorrectPassword"));
            if (passwordRef.current) {
              passwordRef.current.value = "";
              passwordRef.current.focus();
            }
            break;
            
          default:
            setErrorMessage(data.message || t("loginError"));
        }

        // Show error message with animation
        if (errorMessageRef.current) {
          errorMessageRef.current.style.display = "inline-block";
          errorMessageRef.current.style.opacity = "1";
          errorMessageRef.current.classList.add(classes.shake);
          
          // Remove animation class after it completes
          setTimeout(() => {
            errorMessageRef.current?.classList.remove(classes.shake);
          }, 500);
        }
      }
    } catch (error) {
      console.error("Login error:", error);
      setErrorMessage(t("networkError"));
    } finally {
      setIsLoading(false);
    }
  }

  const handleLoginClick = () => navigate("/");
  const handleSignupClick = () => navigate("/signup");

  return (
    <div className={`${classes.container} ${langCtx.lang === "fa" ? classes.rtl : ""}`}>
      <div className={classes.loginBox}>
        <div className={classes.tabRow}>
          <button
            type="button"
            className={`${classes.tabBtn} ${isLoginRoute ? classes.tabBtnActive : ""}`}
            onClick={handleLoginClick}
            disabled={isLoading}
          >
            Login
          </button>
          <button
            type="button"
            className={`${classes.tabBtn} ${!isLoginRoute ? classes.tabBtnActive : ""}`}
            onClick={handleSignupClick}
            disabled={isLoading}
          >
            Sign-up
          </button>
        </div>

        <div className={classes.logo}>
          <img src={images.logo} alt="Trafertir Group" />
        </div>
        <p className={classes.brand}>Trafertir Group</p>
        <h2 className={classes.title}>{t("loginPage")}</h2>

        <form onSubmit={handleLogin}>
          <Input
            ref={userNameRef}
            type="text"
            id="userName"
            placeholder="Username"
            disabled={isLoading}
          />
          <div className={classes.passwordWrap}>
            <Input
              ref={passwordRef}
              type={showPassword ? "text" : "password"}
              id="pass"
              placeholder="Password"
              disabled={isLoading}
            />
            <button
              type="button"
              className={classes.eyeBtn}
              onClick={() => setShowPassword(!showPassword)}
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              <Icon
                icon={showPassword ? "mdi:eye-off" : "mdi:eye"}
                width="20"
                height="20"
              />
            </button>
          </div>

          {errorMessage && (
            <span
              ref={errorMessageRef}
              className={`${classes.errorMessage} ${errorMessage ? classes.visible : ""}`}
              role="alert"
            >
              {errorMessage}
            </span>
          )}

          <Button type="submit" disabled={isLoading}>
            {isLoading ? t("loggingIn") : t("login")}
          </Button>

          <div className={classes.checkbox}>
            <input
              type="checkbox"
              id="rememberMe"
              disabled={isLoading}
            />
            <label htmlFor="rememberMe">{t("rememberMe")}</label>
          </div>
        </form>
      </div>

      <div className={classes.keyPic}>
        <img
          src={require("../../assets/images/Revenue-cuate.svg").default}
          alt=""
        />
      </div>
    </div>
  );
}

export default LoginBox;
